import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../axios/axiosInstance";
import { getEmployees } from "services/employees";
import { getContractors } from "services/contractors";
import { getWorkplaces } from "services/workplaces";
import { getActiveCompanyId } from "utils/companyContext";

const companyId = () => getActiveCompanyId();

const toIso = (date, time) => {
    if (!date || !time) return null;
    return new Date(`${date}T${time}:00`).toISOString();
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const nowTimeStr = () => new Date().toTimeString().slice(0, 5);
const oneHourLaterStr = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    return d.toTimeString().slice(0, 5);
};

function AddTimeCard() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        employeeId: "",
        workplaceId: "",
        clockInDate: todayStr(),
        clockInTime: nowTimeStr(),
        clockOutDate: todayStr(),
        clockOutTime: oneHourLaterStr(),
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const { data: allEmployees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees });
    const { data: allContractors = [] } = useQuery({ queryKey: ["contractors"], queryFn: getContractors });
    const { data: workplacesRaw = [] } = useQuery({ queryKey: ["workplaces"], queryFn: getWorkplaces });
    const workplaces = Array.isArray(workplacesRaw) ? workplacesRaw : (workplacesRaw?.data ?? []);

    // Only show hourly employees — salaried employees don't need timecards
    const employees = allEmployees.filter(e => (e.payType || '').toLowerCase() === 'hourly');
    // Only show hourly contractors — flat-fee/weekly/monthly contractors don't clock in
    const contractors = Array.isArray(allContractors)
        ? allContractors.filter(c => (c.payType || '').toLowerCase() === 'hourly')
        : [];

    const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!form.employeeId) return setError("Please select an employee or contractor.");
        if (!form.clockInDate || !form.clockInTime) return setError("Clock-in date and time are required.");
        if (!form.clockOutDate || !form.clockOutTime) return setError("Clock-out date and time are required.");

        const clockInAt  = toIso(form.clockInDate, form.clockInTime);
        const clockOutAt = toIso(form.clockOutDate, form.clockOutTime);
        if (new Date(clockOutAt) <= new Date(clockInAt)) {
            return setError("Clock-out time must be later than clock-in time. Please adjust the times and try again.");
        }

        setSubmitting(true);
        try {
            const cid = companyId();

            // Determine if selected person is a contractor or employee
            const isContractor = contractors.some(c => (c.id || c.contractorId) === form.employeeId);
            await axiosInstance.post(`/companies/${cid}/timecards/manual`, {
                ...(isContractor
                    ? { contractorId: form.employeeId }
                    : { employeeId: form.employeeId }),
                workplaceId: form.workplaceId || null,
                clockInAt:   clockInAt,
                clockOutAt:  clockOutAt,
                notes:       form.notes || null,
            });

            setSuccess(true);
            setTimeout(() => navigate(-1), 1500);
        } catch (err) {
            const msg = err?.response?.data?.message
                || err?.response?.data?.errors?.[0]
                || err?.response?.data
                || "Failed to create timecard. Please try again.";
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="content" style={{ padding: "40px 24px" }}>
                <div className="text-center py-5">
                    <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
                    <h4 style={{ color: "#22c55e", fontWeight: 700 }}>Timecard Added</h4>
                    <p style={{ color: "#6c757d" }}>Redirecting to Time & Attendance...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content" style={{ padding: "16px 24px 40px 16px" }}>
            <div className="payroll-section">
                <div className="payroll-details-section mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <h2 className="main-title mb-0">Add Timecard</h2>
                            <p className="title mt-1" style={{ color: "#6c757d" }}>
                                Manually record clock-in and clock-out for hourly employees and contractors.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </button>
                    </div>

                    {error && (
                        <div className="alert alert-danger" style={{ borderRadius: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Employee & Workplace */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Employee / Contractor Information</h3>
                            {employees.length === 0 && contractors.length === 0 && (
                                <div className="alert alert-info" style={{ fontSize: 13, borderRadius: 8, marginBottom: 12 }}>
                                    No hourly employees or contractors found. Only hourly workers require timecards.
                                </div>
                            )}
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>
                                        Hourly Employee / Contractor <span style={{ color: "#e87722" }}>*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={form.employeeId}
                                        onChange={set("employeeId")}
                                        required
                                    >
                                        <option value="">Select person...</option>
                                        {employees.length > 0 && (
                                            <optgroup label="── Hourly Employees ──">
                                                {employees.map((emp) => (
                                                    <option key={emp.id} value={emp.id}>
                                                        {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.email}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                        {contractors.length > 0 && (
                                            <optgroup label="── Hourly Contractors ──">
                                                {contractors.map((c) => (
                                                    <option key={c.id || c.contractorId} value={c.id || c.contractorId}>
                                                        {[c.firstName, c.lastName].filter(Boolean).join(" ") || c.businessName || c.email}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                </div>
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Workplace (optional)</label>
                                    <select
                                        className="form-select"
                                        value={form.workplaceId}
                                        onChange={set("workplaceId")}
                                    >
                                        <option value="">No specific workplace</option>
                                        {workplaces.map((w) => (
                                            <option key={w.id} value={w.id}>{w.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Clock-in */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Clock-In</h3>
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Date <span style={{ color: "#e87722" }}>*</span></label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={form.clockInDate}
                                        onChange={set("clockInDate")}
                                        required
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Time <span style={{ color: "#e87722" }}>*</span></label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={form.clockInTime}
                                        onChange={set("clockInTime")}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Clock-out */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Clock-Out</h3>
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Date <span style={{ color: "#e87722" }}>*</span></label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={form.clockOutDate}
                                        onChange={set("clockOutDate")}
                                        required
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Time <span style={{ color: "#e87722" }}>*</span></label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={form.clockOutTime}
                                        onChange={set("clockOutTime")}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Notes (optional)</h3>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="e.g. Employee forgot to clock in/out"
                                value={form.notes}
                                onChange={set("notes")}
                                style={{ resize: "vertical" }}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-3 mt-2">
                            <button
                                type="submit"
                                className="button-orange"
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : "Add Timecard"}
                            </button>
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AddTimeCard;
